export type CanvasAssignmentCandidateInput = {
  sourceItemKey: string;
  sourceCourseKey: string;
  courseTitle: string;
  courseColor?: string;
  sourceItemType?: "assignment" | "quiz" | "discussion";
  title: string;
  dueAt?: string;
  sourceUrl?: string;
};

export type ParsedCanvasSchedule = {
  format: "html" | "mhtml";
  candidates: CanvasAssignmentCandidateInput[];
  duplicatesRemoved: number;
  warnings: string[];
};

function normalizedText(value: string | null | undefined) {
  return value?.replace(/\s+/g, " ").trim() ?? "";
}

function decodeQuotedPrintable(value: string) {
  const unfolded = value.replace(/=\r?\n/g, "");
  let binary = "";
  for (let index = 0; index < unfolded.length; index += 1) {
    if (
      unfolded[index] === "=" &&
      /^[0-9a-f]{2}$/i.test(unfolded.slice(index + 1, index + 3))
    ) {
      binary += String.fromCharCode(
        Number.parseInt(unfolded.slice(index + 1, index + 3), 16),
      );
      index += 2;
      continue;
    }
    binary += unfolded[index];
  }
  return new TextDecoder().decode(
    Uint8Array.from(binary, (character) => character.charCodeAt(0)),
  );
}

function decodedMimeBody(part: string) {
  const separator = part.match(/\r?\n\r?\n/);
  if (separator?.index === undefined) return undefined;
  const headers = part.slice(0, separator.index);
  const body = part.slice(separator.index + separator[0].length);
  if (/Content-Transfer-Encoding:\s*quoted-printable/i.test(headers)) {
    return decodeQuotedPrintable(body);
  }
  if (/Content-Transfer-Encoding:\s*base64/i.test(headers)) {
    try {
      const binary = atob(body.replace(/\s+/g, ""));
      return new TextDecoder().decode(
        Uint8Array.from(binary, (character) => character.charCodeAt(0)),
      );
    } catch {
      return undefined;
    }
  }
  return body;
}

function pageFromMhtml(source: string) {
  const boundary = source
    .match(/boundary=(?:"([^"]+)"|([^\s;]+))/i)
    ?.slice(1)
    .find(Boolean);
  if (boundary === undefined) {
    throw new Error("This MHTML file does not contain a readable boundary.");
  }
  const parts = source.split(`--${boundary}`);
  const htmlPart = parts.find((part) =>
    /Content-Type:\s*text\/html\b/i.test(part),
  );
  if (htmlPart === undefined) {
    throw new Error("This MHTML file does not contain a saved HTML page.");
  }
  const html = decodedMimeBody(htmlPart);
  if (html === undefined) {
    throw new Error("The saved HTML part is incomplete.");
  }
  return {
    html,
    stylesheets: parts
      .filter((part) => /Content-Type:\s*text\/css\b/i.test(part))
      .map(decodedMimeBody)
      .filter((value): value is string => value !== undefined),
  };
}

function canvasEnvironmentFrom(html: string) {
  const names = new Map<string, string>();
  const environmentSource = html.match(
    /\bENV\s*=\s*(\{[\s\S]*?\});\s*BRANDABLE_CSS_HANDLEBARS_INDEX\b/,
  )?.[1];
  if (environmentSource === undefined) return { courseNames: names };
  try {
    const environment = JSON.parse(environmentSource) as {
      TIMEZONE?: unknown;
      CALENDAR?: {
        CONTEXTS?: Array<{ id?: unknown; type?: unknown; name?: unknown }>;
      };
    };
    for (const context of environment.CALENDAR?.CONTEXTS ?? []) {
      if (
        context.type === "course" &&
        typeof context.id === "string" &&
        typeof context.name === "string"
      ) {
        names.set(`course_${context.id}`, normalizedText(context.name));
      }
    }
    return {
      courseNames: names,
      ...(typeof environment.TIMEZONE === "string"
        ? { timezone: environment.TIMEZONE }
        : {}),
    };
  } catch {
    return { courseNames: names };
  }
}

function courseKeyFrom(element: Element) {
  for (const candidate of [element, ...element.querySelectorAll("[class]")]) {
    const courseClass = [...candidate.classList].find((className) =>
      /^group_course_\d+$/.test(className),
    );
    if (courseClass !== undefined) return courseClass.slice("group_".length);
  }
  return "course_unknown";
}

type CourseMetadata = {
  title: string;
  color?: string;
};

function normalizedCourseColor(value: string) {
  const hex = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (hex !== undefined) {
    return `#${
      hex.length === 3
        ? [...hex].map((character) => `${character}${character}`).join("")
        : hex
    }`.toLocaleLowerCase();
  }
  const rgb = value
    .trim()
    .match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
  if (rgb === null) return undefined;
  const channels = rgb.slice(1).map(Number);
  if (channels.some((channel) => channel > 255)) return undefined;
  return `#${channels
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function courseColorsFrom(stylesheets: string[]) {
  const colors = new Map<string, string>();
  for (const stylesheet of stylesheets) {
    for (const rule of stylesheet.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
      const courseKeys = [
        ...new Set(
          [...(rule[1] ?? "").matchAll(/\.group_(course_\d+)/g)].map(
            (match) => match[1],
          ),
        ),
      ];
      const colorValue = (rule[2] ?? "").match(
        /(?:^|;)\s*(?:color|border-color|background-color)\s*:\s*([^;]+)/i,
      )?.[1];
      const color =
        colorValue === undefined
          ? undefined
          : normalizedCourseColor(colorValue);
      if (color === undefined) continue;
      for (const courseKey of courseKeys) {
        if (courseKey !== undefined && !colors.has(courseKey)) {
          colors.set(courseKey, color);
        }
      }
    }
  }
  return colors;
}

function courseMetadataFrom(
  document: Document,
  environmentNames: ReadonlyMap<string, string>,
  stylesheets: string[],
) {
  const metadata = new Map<string, CourseMetadata>();
  for (const [courseKey, title] of environmentNames) {
    metadata.set(courseKey, { title });
  }
  for (const item of document.querySelectorAll<HTMLElement>(
    "li[data-context^='course_']",
  )) {
    const courseKey = item.dataset.context;
    const title = normalizedText(item.querySelector("label")?.textContent);
    if (
      courseKey === undefined ||
      !/^course_\d+$/.test(courseKey) ||
      title === ""
    )
      continue;
    metadata.set(courseKey, { title });
  }
  const inlineStyles = [...document.querySelectorAll("style")].map(
    (style) => style.textContent ?? "",
  );
  for (const [courseKey, color] of courseColorsFrom([
    ...inlineStyles,
    ...stylesheets,
  ])) {
    const current = metadata.get(courseKey);
    if (current !== undefined) metadata.set(courseKey, { ...current, color });
  }
  return metadata;
}

function sourceItemTypeFrom(element: Element) {
  if (element.querySelector(".icon-quiz") !== null) return "quiz" as const;
  if (element.querySelector(".icon-discussion") !== null)
    return "discussion" as const;
  if (
    element.querySelector(".icon-assignment") !== null ||
    element.classList.contains("assignment")
  )
    return "assignment" as const;
  return undefined;
}

const monthNumbers = new Map(
  [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ].flatMap(
    (month, index) =>
      [
        [month, index + 1],
        [month.slice(0, 3), index + 1],
      ] as const,
  ),
);

type CalendarDate = {
  year: number;
  month: number;
  day: number;
};

function calendarDateFromText(value: string, referenceDate?: Date) {
  const normalized = normalizedText(value);
  const isoDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate !== null) {
    return {
      year: Number(isoDate[1]),
      month: Number(isoDate[2]),
      day: Number(isoDate[3]),
    };
  }
  const dayMonth = normalized.match(
    /\b(\d{1,2})\s+(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)(?:\s*,?\s+(\d{4}))?\b/i,
  );
  if (dayMonth === null) return undefined;
  const day = Number(dayMonth[1]);
  const month = monthNumbers.get(dayMonth[2]?.toLocaleLowerCase() ?? "");
  if (month === undefined || day < 1 || day > 31) return undefined;
  const explicitYear =
    dayMonth[3] === undefined ? undefined : Number(dayMonth[3]);
  if (explicitYear !== undefined) return { year: explicitYear, month, day };

  if (referenceDate === undefined || Number.isNaN(referenceDate.getTime()))
    return undefined;
  const referenceYear = referenceDate.getUTCFullYear();
  return [referenceYear - 1, referenceYear, referenceYear + 1]
    .map((year) => ({ year, month, day }))
    .filter((candidate) =>
      Number.isFinite(
        Date.UTC(candidate.year, candidate.month - 1, candidate.day),
      ),
    )
    .sort(
      (left, right) =>
        Math.abs(
          Date.UTC(left.year, left.month - 1, left.day) -
            referenceDate.getTime(),
        ) -
        Math.abs(
          Date.UTC(right.year, right.month - 1, right.day) -
            referenceDate.getTime(),
        ),
    )[0];
}

function dueAtFrom(date: CalendarDate, timeText: string, timezone?: string) {
  const time = timeText.match(/(?:^|\s)(\d{1,2}):(\d{2})\s*(am|pm)?\b/i);
  if (time === null) return undefined;
  let hour = Number(time[1]);
  const minute = Number(time[2]);
  const meridiem = time[3]?.toLocaleLowerCase();
  if (minute > 59 || hour > 23 || (hour === 0 && meridiem !== undefined))
    return undefined;
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  const target = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
  if (timezone === undefined) return new Date(target).toISOString();
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    let guess = target;
    for (let pass = 0; pass < 2; pass += 1) {
      const parts = Object.fromEntries(
        formatter
          .formatToParts(new Date(guess))
          .map((part) => [part.type, part.value]),
      );
      const represented = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second),
      );
      guess += target - represented;
    }
    return new Date(guess).toISOString();
  } catch {
    return undefined;
  }
}

function calendarDataStart(element: Element) {
  const value = element
    .querySelector("[data-start]")
    ?.getAttribute("data-start");
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function calendarTimeFrom(element: Element) {
  return normalizedText(
    element.querySelector("[data-start]")?.getAttribute("data-start"),
  );
}

function calendarReferenceDate(document: Document) {
  const cell =
    document.querySelector<HTMLElement>(".fc-day.fc-today[data-date]") ??
    document.querySelector<HTMLElement>(
      ".fc-day[data-date]:not(.fc-other-month)",
    ) ??
    document.querySelector<HTMLElement>(".fc-day[data-date]");
  const value = cell?.dataset.date;
  if (value === undefined) return undefined;
  const parsed = new Date(`${value}T12:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function calendarDateForEvent(event: Element, document: Document) {
  const cell = event.closest<HTMLTableCellElement>("td.fc-event-container");
  const week = event.closest(".fc-row.fc-week");
  if (cell === null || cell === undefined || week === null) return undefined;
  const weeks = [
    ...document.querySelectorAll(".fc-day-grid > .fc-row.fc-week"),
  ];
  const rowIndex = weeks.indexOf(week);
  if (rowIndex < 0) return undefined;
  const dayRows = [
    ...document.querySelectorAll<HTMLTableRowElement>(
      ".fc-day-grid .fc-bg tbody > tr",
    ),
  ];
  const dayCell = dayRows[rowIndex]?.cells[cell.cellIndex];
  return calendarDateFromText(dayCell?.dataset.date ?? "");
}

function agendaDateText(item: Element) {
  const container = item.closest(".agenda-event__container");
  const previous = container?.previousElementSibling;
  if (previous?.matches(".agenda-day")) {
    return normalizedText(previous.querySelector(".agenda-date")?.textContent);
  }
  return normalizedText(
    item.closest(".agenda-day")?.querySelector(".agenda-date")?.textContent,
  );
}

function assignmentUrl(element: Element) {
  const href = element.closest("a[href]")?.getAttribute("href");
  if (href === null || href === undefined || href === "") return undefined;
  try {
    const url = new URL(href);
    if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

function agendaCandidates(
  document: Document,
  courses: ReadonlyMap<string, CourseMetadata>,
  timezone?: string,
  referenceDate?: Date,
) {
  const candidates: CanvasAssignmentCandidateInput[] = [];
  for (const item of document.querySelectorAll<HTMLElement>(
    ".agenda-event__item[data-event-id]",
  )) {
    const sourceItemType = sourceItemTypeFrom(item);
    if (sourceItemType === undefined) continue;
    const sourceItemKey = normalizedText(item.dataset.eventId);
    const title = normalizedText(
      item.querySelector(".agenda-event__title")?.textContent,
    );
    if (sourceItemKey === "" || title === "") continue;
    const sourceCourseKey = courseKeyFrom(item);
    const course = courses.get(sourceCourseKey);
    const timeText = normalizedText(
      item.querySelector(".agenda-event__time")?.textContent,
    );
    const dueAt = (() => {
      const date = calendarDateFromText(agendaDateText(item), referenceDate);
      return date === undefined
        ? undefined
        : dueAtFrom(date, timeText, timezone);
    })();
    const sourceUrl = assignmentUrl(
      item.querySelector(".agenda-event__title")!,
    );
    candidates.push({
      sourceItemKey,
      sourceCourseKey,
      courseTitle: course?.title ?? "Course name unavailable",
      ...(course?.color === undefined ? {} : { courseColor: course.color }),
      sourceItemType,
      title,
      ...(dueAt === undefined ? {} : { dueAt }),
      ...(sourceUrl === undefined ? {} : { sourceUrl }),
    });
  }
  return candidates;
}

function monthCandidates(
  document: Document,
  courses: ReadonlyMap<string, CourseMetadata>,
  timezone?: string,
) {
  const candidates: CanvasAssignmentCandidateInput[] = [];
  for (const event of document.querySelectorAll<HTMLElement>(".fc-event")) {
    const sourceItemType = sourceItemTypeFrom(event);
    if (sourceItemType === undefined) continue;
    const title = normalizedText(event.querySelector(".fc-title")?.textContent);
    if (title === "") continue;
    const sourceCourseKey = courseKeyFrom(event);
    const course = courses.get(sourceCourseKey);
    const dateText =
      event.closest<HTMLElement>("[data-date]")?.dataset.date ?? "";
    const timeText = normalizedText(
      event.querySelector(".fc-time")?.textContent,
    );
    const dueAt =
      calendarDataStart(event) ??
      (() => {
        const date =
          calendarDateForEvent(event, document) ??
          calendarDateFromText(dateText);
        const canvasTime = calendarTimeFrom(event);
        return date === undefined
          ? undefined
          : dueAtFrom(
              date,
              canvasTime === "" ? timeText : canvasTime,
              timezone,
            );
      })();
    const sourceUrl = assignmentUrl(event);
    const sourceItemKey = normalizedText(
      event.dataset.eventId ||
        event.id ||
        `${sourceCourseKey}:${title.toLocaleLowerCase()}:${dueAt ?? dateText}`,
    );
    candidates.push({
      sourceItemKey,
      sourceCourseKey,
      courseTitle: course?.title ?? "Course name unavailable",
      ...(course?.color === undefined ? {} : { courseColor: course.color }),
      sourceItemType,
      title,
      ...(dueAt === undefined ? {} : { dueAt }),
      ...(sourceUrl === undefined ? {} : { sourceUrl }),
    });
  }
  return candidates;
}

export function parseCanvasScheduleText(input: {
  fileName: string;
  text: string;
  timezone?: string;
}): ParsedCanvasSchedule {
  const format = /\.mhtml?$/i.test(input.fileName) ? "mhtml" : "html";
  const page =
    format === "mhtml"
      ? pageFromMhtml(input.text)
      : { html: input.text, stylesheets: [] };
  const html = page.html;
  const document = new DOMParser().parseFromString(html, "text/html");
  const environment = canvasEnvironmentFrom(html);
  const courses = courseMetadataFrom(
    document,
    environment.courseNames,
    page.stylesheets,
  );
  const timezone = environment.timezone ?? input.timezone;
  const referenceDate = calendarReferenceDate(document);
  const extracted = agendaCandidates(
    document,
    courses,
    timezone,
    referenceDate,
  );
  if (extracted.length === 0) {
    extracted.push(...monthCandidates(document, courses, timezone));
  }
  const candidatesBySource = new Map<string, CanvasAssignmentCandidateInput>();
  for (const candidate of extracted) {
    candidatesBySource.set(candidate.sourceItemKey, candidate);
  }
  const candidates = [...candidatesBySource.values()];
  const warnings: string[] = [];
  const missingDueDates = candidates.filter(
    (candidate) => candidate.dueAt === undefined,
  ).length;
  const missingCourseNames = candidates.filter(
    (candidate) => candidate.courseTitle === "Course name unavailable",
  ).length;
  if (missingDueDates > 0) {
    warnings.push(
      `${missingDueDates} assignment${missingDueDates === 1 ? " has" : "s have"} no readable due date.`,
    );
  }
  if (missingCourseNames > 0) {
    warnings.push(
      `${missingCourseNames} assignment${missingCourseNames === 1 ? " has" : "s have"} no readable Course name.`,
    );
  }
  if (candidates.length === 0) {
    warnings.push("No Canvas assignments were found in this saved page.");
  }
  return {
    format,
    candidates,
    duplicatesRemoved: extracted.length - candidates.length,
    warnings,
  };
}
